import { Link } from "react-router-dom";
import { useEffect, useRef } from "react";
import * as Icons from "lucide-react";
import { ArrowRight, Building2, Users } from "lucide-react";
import { imageUrl } from "@/lib/imageUrl";
import { visualForCompanyCard } from "@/data/companyMenuVisualData";
import "./mega-menu.css";

/**
 * Company Mega Menu — 4-card pixel-perfect dropdown with bottom CTA.
 * Cards + CTA copy come from Settings (fully dynamic).
 */
export default function CompanyMegaMenu({ sections = [], cta = {}, onClose }) {
  const ref = useRef(null);

  useEffect(() => {
    function onKey(e) { if (e.key === "Escape") onClose?.(); }
    function onClick(e) { if (ref.current && !ref.current.contains(e.target)) onClose?.(); }
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, [onClose]);

  const items = [...sections].slice(0, 4);

  return (
    <div
      role="menu"
      data-testid="company-mega-menu"
      className="absolute left-0 right-0 top-full z-50 flex justify-center px-4"
    >
      <div
        ref={ref}
        className="mm-drop w-full max-w-[1440px] bg-white rounded-[20px] shadow-[0_18px_60px_-12px_rgba(11,31,68,0.22)] border border-slate-100 p-7 pointer-events-auto"
      >
        {/* 4-col card grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {items.map((c, i) => (
            <SectionCard key={c.title || i} c={c} index={i} onClose={onClose} />
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-6 rounded-[14px] bg-[#F3F6FC] border border-[#E2E8F0] px-6 py-5 flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-6">
          <div className="w-14 h-14 shrink-0 rounded-full bg-white border border-[#E2E8F0] flex items-center justify-center text-[#2563EB]" aria-hidden="true">
            <Users className="w-7 h-7" strokeWidth={1.6} />
          </div>
          <div className="flex-1">
            <div className="font-heading font-extrabold text-[#0B1F44] text-[17px] md:text-[19px] leading-snug">
              {cta.headline || "Partner with Shingare Industries"}
            </div>
            <div className="text-[12.5px] text-[#4B5563] mt-1 leading-snug max-w-2xl">
              {cta.text || "Let's build innovative engineering solutions together."}
            </div>
          </div>
          <Link
            to={cta.url || "/contact"}
            onClick={onClose}
            data-testid="cm-cta-button"
            className="group shrink-0 inline-flex items-center gap-2 bg-[#2563EB] hover:bg-[#1D4ED8] text-white px-6 py-3.5 rounded-[8px] text-[12px] font-heading font-extrabold uppercase tracking-widest transition-colors"
          >
            {cta.label || "Contact Us"}
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
          </Link>
        </div>
      </div>
    </div>
  );
}

function SectionCard({ c, index, onClose }) {
  const Icon = Icons[c.icon] || Building2;
  const to = c.url || "/company/about";
  const isExternal = /^https?:\/\//i.test(to);
  // A photo set in Settings wins; the curated default for this card (src/data/companyMenuVisualData.js) fills in when there is none.
  const visual = visualForCompanyCard(to);
  const image = imageUrl(c.image || visual?.image);
  const cls =
    "mm-card group relative flex flex-col overflow-hidden rounded-[16px] border border-[#E2E8F0] bg-white transition-all duration-300 hover:-translate-y-1 hover:border-[#2563EB]/60 hover:shadow-[0_22px_40px_-22px_rgba(37,99,235,0.45)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB]";

  const inner = (
    <>
      {/* Photo: fixed 16:9 so every card is the same height and nothing is stretched. A soft navy overlay unifies photos with
          different lighting; a blue accent line draws across the bottom edge on hover; the photo zooms only slightly. */}
      <div className="relative aspect-[16/9] overflow-hidden bg-[#0B2348]">
        {image ? (
          <img
            src={image}
            alt={c.image_alt || visual?.alt || c.title || ""}
            width="648"
            height="364"
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.03]"
          />
        ) : null}
        <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(7,27,58,0.05)_35%,rgba(7,27,58,0.55)_100%)]" aria-hidden="true" />
        <span className="pointer-events-none absolute inset-x-0 bottom-0 h-[3px] origin-left scale-x-0 bg-[#2563EB] transition-transform duration-500 ease-out group-hover:scale-x-100" aria-hidden="true" />
      </div>

      {/* Body */}
      <div className="flex flex-1 flex-col p-5">
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-[#EEF4FF] text-[#2563EB] transition-colors duration-300 group-hover:bg-[#2563EB] group-hover:text-white"><Icon className="h-[18px] w-[18px]" strokeWidth={1.75} aria-hidden="true" /></span>
          <div className="font-heading text-[16px] font-extrabold leading-tight text-[#0B1F44]">{c.title}</div>
        </div>
        <p className="mt-3 line-clamp-3 text-[13px] leading-[1.55] text-[#4B5563]">{c.description}</p>
        <div className="mt-auto pt-4">
          <span className="inline-flex items-center gap-1.5 text-[11px] font-heading font-extrabold uppercase tracking-widest text-[#2563EB]">
            View More
            <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
          </span>
        </div>
      </div>
    </>
  );

  return isExternal ? (
    <a href={to} target="_blank" rel="noreferrer" data-testid={`cm-card-${slug(c.title)}`} className={cls} style={{ animationDelay: `${index * 55}ms` }}>{inner}</a>
  ) : (
    <Link to={to} onClick={onClose} data-testid={`cm-card-${slug(c.title)}`} className={cls} style={{ animationDelay: `${index * 55}ms` }}>{inner}</Link>
  );
}

function slug(s = "") {
  return String(s).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}
